import clsx from 'clsx';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useOutsideClick } from '@/hooks/use-outside-click';

export type CustomSelectProps = {
    options: { value: string | number; name: string }[];
    value: string | number;
    onChange: (value: string | number) => void;
    label?: string;
    infoText?: string;
    searchable?: boolean;
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    dropdownClass?: clsx.ClassValue;
    showRequiredError?: boolean;
};

export const CustomSelect = ({
    options,
    value,
    onChange,
    label = '',
    infoText,
    required = false,
    disabled = false,
    searchable = false,
    dropdownClass = '',
    showRequiredError = false,
}: CustomSelectProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const focusedContainerRef = useRef<HTMLDivElement>(null);
    const [showInfo, setShowInfo] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectValue, setSelectValue] = useState(value);
    const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
    const selectRef = useOutsideClick(() => setIsFocused(false));
    const focusedItemIndexRef = useRef(focusedItemIndex);
    const isFocusedRef = useRef(isFocused);
    const selectOptionsRef = useRef<typeof options>([]);

    const selectOptions = useMemo(() => {
        const lowerCaseSearchValue = searchValue.toLocaleLowerCase();
        let items = [...options];

        if (lowerCaseSearchValue !== '') {
            items = items
                .filter(o => o.name.toLocaleLowerCase().includes(lowerCaseSearchValue))
                .filter((item, index, self) => index === self.findIndex(t => t.value === item.value && t.name === item.name));
        }

        selectOptionsRef.current = items;
        return items;
    }, [options, searchValue]);

    const currentValue = useMemo(() => (value ? selectOptions.find(option => option.value === value)?.name : ''), [value, selectOptions]);

    useEffect(() => {
        isFocusedRef.current = isFocused;
        focusedItemIndexRef.current = focusedItemIndex;

        containerRef.current?.closest('div[data-component="section"]')?.classList.remove('r-overflow-hidden');

        if (!isFocused) {
            containerRef.current?.closest('div[data-component="section"]')?.classList.add('r-overflow-hidden');
            setFocusedItemIndex(0);
        }
    }, [focusedItemIndex, isFocused]);

    useEffect(() => {
        containerRef.current?.addEventListener('keydown', keyDown);
        return () => {
            containerRef.current?.removeEventListener('keydown', keyDown);
        };
    }, []);

    useEffect(() => {
        if (isFocused && searchable) {
            inputRef?.current?.focus();
        }
    }, [isFocused, searchable]);

    const keyDown = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setIsFocused(false);
                setSearchValue('');
                focusedContainerRef.current?.blur();
                break;

            case 'ArrowDown':
                e.preventDefault();

                if (!isFocusedRef.current) {
                    setIsFocused(true);
                }

                if (focusedItemIndexRef.current === null) {
                    setFocusedItemIndex(0);
                    return;
                }

                if (focusedItemIndexRef.current !== null && focusedItemIndexRef.current < selectOptionsRef.current.length) {
                    setFocusedItemIndex(focusedItemIndexRef.current + 1);
                }

                break;

            case 'ArrowUp':
                e.preventDefault();

                if (!isFocusedRef.current) {
                    setIsFocused(true);
                }

                if (focusedItemIndexRef.current === null) {
                    setFocusedItemIndex(selectOptions.length - 1);
                    return;
                }

                if (focusedItemIndexRef.current !== null && focusedItemIndexRef.current < selectOptionsRef.current.length) {
                    setFocusedItemIndex(focusedItemIndexRef.current - 1);
                }

                break;

            case 'Enter':
                e.preventDefault();

                if (isFocusedRef.current === false) {
                    // setIsFocused(true);
                    return;
                }

                if (focusedItemIndexRef.current !== null) {
                    const option = selectOptionsRef.current[focusedItemIndexRef.current];
                    if (option?.value) {
                        onSelectValue(option.value);
                    }
                }

                break;
        }
    };

    const onSelectValue = (value: string | number) => {
        setIsFocused(false);
        setSearchValue('');
        setSelectValue(value);
        onChange(value);
        focusedContainerRef.current?.blur();
    };

    return (
        <div
            ref={containerRef}
            role="combobox"
            aria-label={label}
            aria-haspopup="listbox"
            data-component="custom-select"
            className="r-relative r-rounded-xl"
            aria-expanded={isFocused && !disabled}
        >
            <div
                tabIndex={0}
                ref={focusedContainerRef}
                className={clsx('r-relative r-rounded-xl', {
                    'r-cursor-pointer': !disabled,
                })}
            >
                {label ? (
                    <span
                        onClick={e => {
                            e.stopPropagation();
                            setIsFocused(true);
                        }}
                        className={clsx('r-absolute r-left-4 r-z-10 r-leading-none r-text-gray-500 r-transition-all', {
                            'r-top-2 r-text-xs': !!value || (isFocused && searchable),
                            'r-text-md r-top-[1.15rem]': !value && (!isFocused || !searchable),
                        })}
                    >
                        {label} {required ? '*' : ''}
                    </span>
                ) : null}
                <div
                    onClick={e => {
                        e.stopPropagation();
                        setIsFocused(true);
                    }}
                    className={clsx(
                        'r-pointer r-block r-w-full r-rounded-xl r-border r-border-gray-400 r-px-4 r-pb-1 r-pt-6 focus-within:r-outline-0 focus:r-outline-0 focus-visible:r-outline-0',
                        {
                            'r-text-transparent focus:r-outline-none': !searchable && !value,
                            'r-bg-gray-100 r-text-gray-600': disabled,
                            'r-bg-red-100': showRequiredError && !disabled,
                            'r-bg-white': !disabled && !showRequiredError,
                        }
                    )}
                >
                    {searchable && isFocused && !disabled ? (
                        <input
                            ref={inputRef}
                            value={searchValue}
                            disabled={disabled}
                            className={clsx('r-block r-outline-none', {
                                'r-bg-red-100': showRequiredError,
                            })}
                            onChange={({ target }) => setSearchValue(target.value)}
                        />
                    ) : (
                        <span className="r-block r-min-h-6">{currentValue}</span>
                    )}
                </div>
                <img
                    width={18}
                    height={18}
                    src="/assets/icons/field-help.svg"
                    onClick={() => setShowInfo(!showInfo)}
                    className={clsx('r-absolute r-right-[10px] r-top-[16px]', {
                        'r-hidden': !infoText,
                    })}
                />
                <img
                    width={10}
                    height={10}
                    alt="arrow"
                    src="/assets/icons/select-arrow.svg"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsFocused(true);
                    }}
                    className={clsx('r-absolute r-top-[1.35rem]', infoText ? 'r-right-9' : 'r-right-[10px]', {
                        'r-rotate-180': !isFocused || disabled,
                    })}
                />
                {showInfo ? <span className="r-mt-2 r-block r-rounded-lg r-bg-slate-200 r-p-4 r-text-sm">{infoText}</span> : null}
            </div>
            {isFocused && !disabled ? (
                <div
                    role="listbox"
                    ref={selectRef}
                    className={clsx(
                        'r-absolute r-left-0 r-right-0 r-z-20 r-mt-2 r-max-h-[26rem] r-overflow-auto r-rounded-xl r-border r-border-gray-400 r-bg-white r-shadow-lg',
                        dropdownClass
                    )}
                >
                    {selectOptions.map(({ value, name }, index) => (
                        <button
                            key={index}
                            role="option"
                            aria-label={name}
                            onClick={() => onSelectValue(value as string)}
                            className={clsx(
                                'r-block r-w-full r-cursor-pointer r-border-b r-border-b-gray-400 r-p-4 r-text-left first:r-rounded-t-xl last:r-rounded-b-xl last:r-border-b-0 hover:r-bg-gray-100 focus:r-bg-gray-100',
                                {
                                    'r-font-semibold': value === selectValue,
                                    'r-bg-gray-100': focusedItemIndex === index,
                                }
                            )}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};
